import { API_BASE_URL } from '../config';

const HELP_URL = '/help';
const HELPDESK_URL = '/helpdesk';

/**
 * Get all help pages
 */
export const getHelpPages = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/pages`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching help pages:', error);
    throw error;
  }
};

/**
 * Get single help page
 */
export const getHelpPage = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/${id}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching help page:', error);
    throw error;
  }
};

/**
 * Create new help page
 */
export const createHelpPage = async (pageData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pageData)
    });
    return await response.json();
  } catch (error) {
    console.error('Error creating help page:', error);
    throw error;
  }
};

/**
 * Update help page
 */
export const updateHelpPage = async (id, pageData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pageData)
    });
    return await response.json();
  } catch (error) {
    console.error('Error updating help page:', error);
    throw error;
  }
};

/**
 * Delete help page
 */
export const deleteHelpPage = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/${id}`, {
      method: 'DELETE'
    });
    return await response.json();
  } catch (error) {
    console.error('Error deleting help page:', error);
    throw error;
  }
};

/**
 * Get help tickets metadata only for the dashboard - NO ticket contents
 * This function ONLY returns basic ticket information needed for the listing page
 */
export const getTickets = async () => {
  try {
    // Use an explicit query parameter to tell the server we only want metadata
    // This avoids any large HTML content being sent over the network
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets?metadata_only=1`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    // Try to parse JSON response
    const rawText = await response.text();
    let data;
    
    try {
      // Parse JSON response
      data = JSON.parse(rawText);
    } catch (jsonError) {
      console.error('Invalid JSON response:', jsonError);
      // Return default structure instead of throwing
      return {
        status: 0,
        message: "Invalid response format",
        tickets: []
      };
    }
    
    // Create a normalized structure with only the necessary fields
    // The landing page ONLY needs this basic information
    let ticketsList = [];
    
    // Handle different response formats by extracting just what we need
    // The new API returns: { status: 1, message: "...", tickets: [...] }
    if (data.tickets && Array.isArray(data.tickets)) {
      ticketsList = data.tickets;
    } else if (Array.isArray(data)) {
      ticketsList = data;
    } else if (data.data && Array.isArray(data.data)) {
      ticketsList = data.data;
    } else if (data.data && data.data.tickets && Array.isArray(data.data.tickets)) {
      ticketsList = data.data.tickets;
    }
    
    // Create a standardized response with ONLY the fields needed for the dashboard
    const normalizedData = {
      status: 1,
      message: "Tickets retrieved",
      tickets: ticketsList.map(ticket => {
        // Extract ONLY the fields we need for the dashboard
        // EXPLICITLY exclude all body/content fields to ensure they never reach the UI
        return {
          ticket_id: ticket.ticket_id,
          ticket_number: ticket.ticket_number,
          ticket_subject: ticket.ticket_subject, 
          ticket_priority: ticket.ticket_priority,
          ticket_status: ticket.ticket_status,
          ticket_date: ticket.ticket_date,
          ticket_assigned: ticket.ticket_assigned,
          ticket_assigned_name: ticket.ticket_assigned_name || '',
          ticket_department: ticket.ticket_department,
          ticket_type: ticket.ticket_type,
          ticket_time: ticket.ticket_time,
          last_update: ticket.last_update,
          // Include user information for originator display
          user_full_name: ticket.user_full_name,
          supplier_user_name: ticket.supplier_user_name,
          ticket_sender: ticket.ticket_sender, // Email sender name for imported tickets
          ticket_user_type: ticket.ticket_user_type,
          ticket_reply: ticket.ticket_reply
          // DELIBERATELY OMITTING:
          // - ticket_body, ticket_content, ticket_description
          // - ticket_html, ticket_message, content, body
          // - Any other fields that might contain large content
        };
      }),
      // Copy any system or date information needed for dashboard charts
      systems: data.systems || data.data?.systems || {},
      dates: data.dates || data.data?.dates || {}
    };
    
    return normalizedData;
  } catch (error) {
    console.error('Error fetching tickets:', error);
    // Return empty data structure instead of throwing to prevent UI errors
    return {
      status: 0,
      message: "Error fetching tickets: " + error.message,
      tickets: []
    };
  }
};

/**
 * Get ticket statistics for dashboard charts and widgets
 * Optimized to only request numerical data without any ticket content
 */
export const getTicketStats = async () => {
  try {
    // Request only stats without any ticket content
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/stats?stats_only=1`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    // Get response as text first to handle potential parsing issues
    const rawText = await response.text();
    let data;
    
    try {
      // Try to parse the response as JSON
      data = JSON.parse(rawText);
    } catch (jsonError) {
      console.error('Invalid JSON response from stats endpoint:', jsonError);
      // If parsing fails, return default stats
      return createDefaultStats();
    }
    
    // Create a normalized stats structure with only numerical data
    let normalizedData = createDefaultStats();
    
    // Apply any numerical stats from the API response (no HTML content)
    if (data) {
      normalizedData.status = 1;
      
      // Handle ticket counts
      if (data.tickets) {
        normalizedData.tickets = {
          open: parseInt(data.tickets.open || 0),
          not_assigned: parseInt(data.tickets.not_assigned || 0),
          provider: parseInt(data.tickets.provider || 0),
          closed: parseInt(data.tickets.closed || 0)
        };
      }
      
      // Handle status counts for charts
      if (data.status_counts) {
        normalizedData.status_counts = {};
        Object.keys(data.status_counts).forEach(key => {
          normalizedData.status_counts[key] = parseInt(data.status_counts[key] || 0);
        });
      } else {
        // Create from ticket counts if status_counts not provided
        normalizedData.status_counts = {
          'Open': normalizedData.tickets.open,
          'Unassigned': normalizedData.tickets.not_assigned,
          'Provider': normalizedData.tickets.provider,
          'Closed': normalizedData.tickets.closed
        };
      }
      
      // Handle system stats (if available)
      if (data.systems) {
        normalizedData.systems = data.systems;
      }
      
      // Handle date-based stats for timeline charts
      if (data.dates) {
        normalizedData.dates = data.dates;
      }
    }
    
    return normalizedData;
  } catch (error) {
    console.error('Error fetching ticket statistics:', error);
    return createDefaultStats(); // Return default stats instead of throwing
  }
};

/**
 * Creates a default stats object for fallback when API returns invalid data
 */
function createDefaultStats() {
  return {
    status: 0,
    message: "Generated default statistics",
    tickets: {
      open: 0,
      not_assigned: 0,
      provider: 0,
      closed: 0
    },
    status_counts: {
      'Open': 0,
      'Unassigned': 0,
      'Provider': 0, 
      'Closed': 0
    },
    systems: {},
    dates: {}
  };
}

/**
 * Get ticket approval stats
 */
export const getTicketApprovalStats = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/workflows/pending`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching approval statistics:', error);
    throw error;
  }
};

/**
 * Get IT users
 */
export const getITUsers = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/agents`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching IT users:', error);
    throw error;
  }
};

/**
 * Get ticket details including full content
 * This is ONLY called when viewing a single ticket's details, not for the dashboard
 */
export const getTicketDetails = async (ticketId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets/${ticketId}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    // Get response as text first to safely handle potential HTML content
    const rawText = await response.text();
    
    try {
      // Try to parse as JSON
      return JSON.parse(rawText);
    } catch (jsonError) {
      console.error('Error parsing ticket details response:', jsonError);
      
      // Return a structured error object that the UI can handle
      return {
        status: 0,
        message: "Error parsing ticket details response. The server might have returned HTML instead of JSON.",
        ticket: {
          ticket_id: ticketId,
          ticket_subject: "Error loading ticket details",
          ticket_status: "unknown"
        }
      };
    }
  } catch (error) {
    console.error('Error fetching ticket details:', error);
    // Return structured error instead of throwing
    return {
      status: 0,
      message: "Error fetching ticket details: " + error.message,
      ticket: {
        ticket_id: ticketId,
        ticket_subject: "Error loading ticket details",
        ticket_status: "unknown"
      }
    };
  }
};

/**
 * Create new ticket
 */
export const createTicket = async (ticketData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/helpdesk/tickets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ticketData)
    });
    return await response.json();
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};

/**
 * Update ticket
 */
export const updateTicket = async (ticketId, ticketData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets/${ticketId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ticketData)
    });
    return await response.json();
  } catch (error) {
    console.error('Error updating ticket:', error);
    throw error;
  }
};

/**
 * Assign ticket to user
 */
export const assignTicket = async (ticketId, assigneeId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets/${ticketId}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        ticket_id: ticketId, 
        assign: assigneeId 
      })
    });
    return await response.json();
  } catch (error) {
    console.error('Error assigning ticket:', error);
    throw error;
  }
};

/**
 * Complete ticket
 */
export const completeTicket = async (ticketId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets/${ticketId}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resolution: 'Ticket completed' })
    });
    return await response.json();
  } catch (error) {
    console.error('Error completing ticket:', error);
    throw error;
  }
};

/**
 * Park/Unpark ticket
 */
export const parkTicket = async (ticketId, park = true) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets/${ticketId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: park ? 'parked' : 'open' })
    });
    return await response.json();
  } catch (error) {
    console.error('Error parking ticket:', error);
    throw error;
  }
};

/**
 * Escalate ticket to external provider
 */
export const escalateTicket = async (ticketId, providerData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets/${ticketId}/escalate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        escalation_type: 'external',
        reason: providerData.details || 'Escalated to external provider',
        provider_email: providerData.email || '',
        cc_emails: providerData.cc || ''
      })
    });
    return await response.json();
  } catch (error) {
    console.error('Error escalating ticket:', error);
    throw error;
  }
};

/**
 * Get pending approvals for dashboard widget (metadata only)
 * This ONLY returns basic approval information for dashboard display
 */
export const getPendingApprovals = async () => {
  try {
    // Request only approval metadata without ticket contents
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/workflows/pending?metadata_only=1`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    // Get response as text first to handle potential parsing issues
    const rawText = await response.text();
    let data;
    
    try {
      // Try to parse the response as JSON
      data = JSON.parse(rawText);
    } catch (jsonError) {
      console.error('Invalid JSON response from approvals endpoint:', jsonError);
      // Return default empty structure if parsing fails
      return {
        status: 0,
        message: "Invalid response format",
        approvals: []
      };
    }
    
    // Create a standardized response with only what's needed for the dashboard widget
    const normalizedData = {
      status: data.status || 0,
      message: data.message || "Approvals retrieved",
      approvals: []
    };
    
    // Extract approvals array, handling different response formats
    let approvalsList = [];
    if (data.approvals && Array.isArray(data.approvals)) {
      approvalsList = data.approvals;
    } else if (Array.isArray(data)) {
      approvalsList = data;
    }
    
    // Map to only include the essential fields needed for the dashboard widget
    normalizedData.approvals = approvalsList.map(approval => ({
      id: approval.id,
      ticket_id: approval.ticket_id,
      ticket_subject: approval.ticket_subject,
      ticket_priority: approval.ticket_priority,
      ticket_date: approval.ticket_date,
      ticket_department: approval.ticket_department,
      level: approval.level,
      status: approval.status
      // DELIBERATELY OMITTING any content/body fields:
      // - ticket_body, ticket_content, ticket_description, etc.
    }));
    
    return normalizedData;
  } catch (error) {
    console.error('Error fetching pending approvals:', error);
    // Return default structure instead of throwing
    return {
      status: 0,
      message: "Error fetching pending approvals: " + error.message,
      approvals: []
    };
  }
};

/**
 * Submit an approval for a ticket
 */
export const approveTicket = async (ticketId, approvalData) => {
  try {
    const { level, status, comment, nextApprover } = approvalData;
    
    // Determine the appropriate approval field based on level
    let approvalField;
    if (level === 'manager') approvalField = 'ticket_manager';
    else if (level === 'hod') approvalField = 'ticket_hod';
    else if (level === 'it_hod') approvalField = 'ticket_it_hod';
    else if (level === 'director') approvalField = 'ticket_director';
    else approvalField = 'ticket_approval';
    
    // Prepare request body
    const requestBody = { 
      ticket_id: ticketId,
      ticket_update: comment || 'Approval submitted',
      [approvalField]: status
    };
    
    // Add next approver if provided
    if (nextApprover) {
      requestBody.notify = nextApprover;
    }
    
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/workflows/${ticketId}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });
    return await response.json();
  } catch (error) {
    console.error('Error submitting approval:', error);
    throw error;
  }
};

/**
 * Get systems for ticket creation
 */
export const getSystems = async () => {
  try {
    // Add pagination parameters to get all systems (backend defaults to page=1, pageSize=20)
    // Use public endpoint to avoid permission issues
    const url = new URL(`${API_BASE_URL}/api/assets/public_systems`);
    url.searchParams.append('page', '1');
    url.searchParams.append('pageSize', '1000'); // Request up to 1000 systems
    
    const response = await fetch(url.toString());
    return await response.json();
  } catch (error) {
    console.error('Error fetching systems:', error);
    throw error;
  }
};

/**
 * Get new ticket form data
 */
export const getNewTicketData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/categories`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching new ticket data:', error);
    throw error;
  }
};

/**
 * Get chat data
 */
export const getChatData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/chat`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching chat data:', error);
    throw error;
  }
};

/**
 * Get chat messages for channel
 */
export const getChatMessages = async (channelId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/chats`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel: channelId })
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching chat messages:', error);
    throw error;
  }
};

/**
 * Send chat message
 */
export const sendChatMessage = async (messageData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(messageData)
    });
    return await response.json();
  } catch (error) {
    console.error('Error sending chat message:', error);
    throw error;
  }
};

/**
 * Get recent chat activity
 */
export const getRecentChats = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/recent`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching recent chats:', error);
    throw error;
  }
};

/**
 * Upload file for chat
 */
export const uploadChatFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/upload`, {
      method: 'POST',
      body: formData
    });
    return await response.json();
  } catch (error) {
    console.error('Error uploading chat file:', error);
    throw error;
  }
};

/**
 * Log chat to ticket
 */
export const logChatToTicket = async (chatData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/log_chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(chatData)
    });
    return await response.json();
  } catch (error) {
    console.error('Error logging chat to ticket:', error);
    throw error;
  }
};

/**
 * Get activity data
 */
export const getActivity = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/activity`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching activity:', error);
    throw error;
  }
};

/**
 * Get new user form data
 */
export const getNewUserData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/new_user`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching new user data:', error);
    throw error;
  }
};

/**
 * Get staff movement form data
 */
export const getStaffMovementData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/staff_movement`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching staff movement data:', error);
    throw error;
  }
};

/**
 * Get access modification form data
 */
export const getAccessModificationData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/access_modification`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching access modification data:', error);
    throw error;
  }
};

/**
 * Get access termination form data
 */
export const getAccessTerminationData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/access_termination`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching access termination data:', error);
    throw error;
  }
};

/**
 * Get approval history for a ticket
 */
export const getApprovalHistory = async (ticketId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets/${ticketId}/approvals`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching approval history:', error);
    throw error;
  }
};

/**
 * Get next approver in the workflow
 */
export const getNextApprover = async (ticketId, currentLevel) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/workflows/next_approver`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        ticket_id: ticketId,
        current_level: currentLevel
      })
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching next approver:', error);
    throw error;
  }
};

/**
 * Check if current user can approve a ticket
 */
export const canApprove = async (ticketId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets/${ticketId}/can_approve`);
    return await response.json();
  } catch (error) {
    console.error('Error checking approval permission:', error);
    throw error;
  }
};

/**
 * Get escalated tickets metadata for dashboard display
 * Only fetches basic information needed for the escalated tickets widget
 */
export const getEscalatedTickets = async () => {
  try {
    // Request only escalated tickets with minimal metadata
    // Use a filter parameter to request only escalated tickets from the server
    const response = await fetch(`${API_BASE_URL}/api${HELPDESK_URL}/tickets?status=escalated&metadata_only=1`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    // Get response as text first to handle potential parsing issues
    const rawText = await response.text();
    let data;
    
    try {
      // Try to parse the response as JSON
      data = JSON.parse(rawText);
    } catch (jsonError) {
      console.error('Invalid JSON response from escalated tickets endpoint:', jsonError);
      // Return empty array if parsing fails
      return [];
    }
    
    // Extract tickets from whatever structure the API returns
    // The new API returns: { status: 1, message: "...", tickets: [...] }
    let allTickets = [];
    if (data.tickets && Array.isArray(data.tickets)) {
      allTickets = data.tickets;
    } else if (Array.isArray(data)) {
      allTickets = data;
    } else if (data.data && Array.isArray(data.data)) {
      allTickets = data.data;
    } else if (data.data && data.data.tickets && Array.isArray(data.data.tickets)) {
      allTickets = data.data.tickets;
    }
    
    // Apply additional client-side filtering in case server-side filtering isn't working
    const escalatedTickets = allTickets.filter(ticket => 
      ticket.ticket_status === 3 || 
      ticket.ticket_status === '3' ||
      ticket.status === 'escalated' ||
      ticket.status === 'provider'
    );
    
    // Return only essential metadata needed for the dashboard widget
    return escalatedTickets.map(ticket => ({
      ticket_id: ticket.ticket_id,
      ticket_subject: ticket.ticket_subject,
      ticket_priority: ticket.ticket_priority,
      ticket_status: ticket.ticket_status,
      ticket_date: ticket.ticket_date,
      ticket_assigned: ticket.ticket_assigned,
      ticket_department: ticket.ticket_department,
      ticket_provider: ticket.ticket_provider
      // DELIBERATELY OMITTING any content/body fields:
      // - ticket_body, ticket_content, ticket_description, etc.
    }));
  } catch (error) {
    console.error('Error fetching escalated tickets:', error);
    return []; // Return empty array instead of throwing to prevent cascading errors
  }
};

/**
 * Start a new chat
 */
export const startUserChat = async (chatData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/start-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(chatData)
    });
    return await response.json();
  } catch (error) {
    console.error('Error starting chat:', error);
    return { status: 0, message: 'Failed to start chat' };
  }
};

/**
 * Send a message in a chat
 */
export const sendUserMessage = async (messageData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/send-message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(messageData)
    });
    return await response.json();
  } catch (error) {
    console.error('Error sending message:', error);
    return { status: 0, message: 'Failed to send message' };
  }
};

/**
 * Get chat messages
 */
export const getUserChatMessages = async (channelId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api${HELP_URL}/messages/${channelId}`);
    return await response.json();
  } catch (error) {
    console.error('Error getting messages:', error);
    return { status: 0, messages: [] };
  }
};
